import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import { useOpeningDates } from "@hilma/netina2all";

interface ChildrenProps {
	children: React.ReactNode
}

interface ProviderValue {
	settings: SettingsResponse | null,
	setSettings: Function,
	showPurchaseButton: boolean | null,
	timeIsOut: boolean,
	error: boolean,
	isDistributionToday: boolean | undefined
}

interface SettingsResponse {
	siteIsOpen: boolean;
	showCountdown: boolean;
	countdownDate: Date;
	donationTarget: number;
	donationBonusTarget: number;
	freeSalePercent: number;
	currentYear: number;
}

export const SettingsContext = React.createContext<ProviderValue | null>(null)
const MAX_TIMEOUT = 2147483647
export const SettingsProvider: React.ComponentType<ChildrenProps> = ({ children }) => {

	const [settings, setSettings] = useState<SettingsResponse | null>(null);
	const [showPurchaseButton, setShowPurchaseButton] = useState<boolean | null>(null);
	const [error, setError] = useState<boolean>(false);
	const [timeIsOut, setTimeIsOut] = useState<boolean>(false);
	const timer = useRef<NodeJS.Timeout | null>(null);
	const openingDates = useOpeningDates();


	useEffect(() => {
		getSettings()
		return () => { clearTimeout(timer.current as NodeJS.Timeout) }
	}, [])

	const getSettings = async () => {
		try {
			const res = await axios.get('/api/settings/get-settings');
			const diffTimes = new Date(res.data.countdownDate).getTime() - new Date().getTime()

			setSettings(res.data)

			if (res.data.siteIsOpen && diffTimes <= 0) {
				setTimeIsOut(true)
				setShowPurchaseButton(false)
			}

			else if (!res.data.siteIsOpen) {
				setShowPurchaseButton(false)
				if (diffTimes > MAX_TIMEOUT || diffTimes <= 0) return;
				timer.current = setTimeout(() => {
					setTimeIsOut(true)
					setShowPurchaseButton(true)
				}, diffTimes);
			}

			else {
				setShowPurchaseButton(true)
				if (diffTimes > MAX_TIMEOUT) return
				timer.current = setTimeout(() => {
					setTimeIsOut(true)
					setShowPurchaseButton(false)
				}, diffTimes)

			}
		} catch (err) {
			setShowPurchaseButton(false)
			setError(true)
		}
	}

	const isDistributionToday = useMemo<boolean | undefined>(() => {
		if (!openingDates) return;
		return openingDates.some(x => moment().isSame(moment(x.date), 'day'));
	}, [openingDates])

	return (
		<SettingsContext.Provider value={{ timeIsOut, showPurchaseButton, settings, setSettings, error, isDistributionToday }} >
			{children}
		</SettingsContext.Provider>
	);
};
